/**
 * Grammar engine — pure logic + HTTP client for server-side grammar
 * checking via /api/grammar/check (LanguageTool backend).
 *
 * Pure functions handle plain-text extraction, offset mapping between
 * ProseMirror positions and LT's character offsets, and conversion of
 * LT matches into PM decoration ranges.
 */

export type SuggestionKind = "spell" | "grammar";
export type GrammarCheckSource =
  | "typing"
  | "edit_fallback"
  | "paste"
  | "preview"
  | "toggle_enabled"
  | "manual_recheck"
  | "draft_stream_complete"
  | "suppression_released";

export interface GrammarMatch {
  offset: number;
  length: number;
  message: string;
  shortMessage: string;
  ruleId: string;
  ruleDescription: string;
  category: string;
  kind: SuggestionKind;
  replacements: string[];
}

export interface GrammarCheckResponse {
  matches: GrammarMatch[];
}

export interface GrammarProgressChunk {
  type: "chunk";
  chunkIndex: number;
  chunkCount: number;
  offset: number;
  length: number;
  matches: GrammarMatch[];
  elapsedMs?: number;
  nextDelayMs?: number;
  textHash?: string;
  cached?: boolean;
}

export interface GrammarProgressDone {
  type: "done";
  matches: GrammarMatch[];
}

export type GrammarProgressEvent = GrammarProgressChunk | GrammarProgressDone;

export interface UserStyleRule {
  rule_id: string;
  action: "off" | "on" | "strict";
  match_text: string | null;
  document_id?: string | null;
}

export interface GrammarPreferences {
  dictionary: string[];
  styleRules: UserStyleRule[];
}

export interface TextSegment {
  pmStart: number;
  plainStart: number;
  length: number;
}

export interface ExtractedText {
  plainText: string;
  segments: TextSegment[];
}

export interface ExtractedTextWindow extends ExtractedText {
  plainStart: number;
  plainEnd: number;
  pmStart: number;
  pmEnd: number;
}

export interface GrammarCheckCacheKeyOptions {
  text: string;
  language?: string;
  styleRules?: UserStyleRule[];
}

export interface FullGrammarCheckCacheEntry {
  key: string;
  response: GrammarCheckResponse;
}

export interface FullGrammarCheckInFlight {
  key: string;
  promise: Promise<GrammarCheckResponse>;
}

export interface FullGrammarCheckCacheState {
  cache: FullGrammarCheckCacheEntry | null;
  inFlight: FullGrammarCheckInFlight | null;
}

export interface DocumentChangeStats {
  insertedChars: number;
  deletedChars: number;
  largestInsertionChars: number;
  largestDeletionChars: number;
  changed: boolean;
}

type GrammarCheckFn = (
  text: string,
  options?: GrammarCheckOptions
) => Promise<GrammarCheckResponse>;

export interface GrammarCheckOptions {
  language?: string;
  styleRules?: UserStyleRule[];
  source?: GrammarCheckSource;
  signal?: AbortSignal | null;
}

const BLOCK_SEPARATOR = "\n\n";
const GRAMMAR_PROGRESS_CHUNK_MAX_CHARS = 8000;
const GRAMMAR_PROGRESS_CACHE_PREFIX = "grammar-progress-cache:";
const GRAMMAR_PROGRESS_CACHE_MAX_ENTRIES = 20;
const GRAMMAR_PROGRESS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export function extractText(doc: any): ExtractedText {
  let plainText = "";
  const segments: TextSegment[] = [];
  let sawTextSinceBlockEnter = false;

  doc.descendants((node: any, pos: number) => {
    if (node.isText) {
      const text: string = node.text ?? "";
      segments.push({ pmStart: pos, plainStart: plainText.length, length: text.length });
      plainText += text;
      sawTextSinceBlockEnter = true;
      return true;
    }
    if (node.isBlock) {
      // Insert a block separator before each block that comes after a
      // previous block with content. We detect this with sawTextSinceBlockEnter
      // (flipped to true by any text node).
      if (sawTextSinceBlockEnter) {
        plainText += BLOCK_SEPARATOR;
        sawTextSinceBlockEnter = false;
      }
    }
    return true;
  });

  return { plainText, segments };
}

/**
 * Extract the textblock (paragraph / heading / blockquote line) containing
 * `pos`, with absolute PM `pmStart` offsets so the resulting matches can
 * be merged back into the document's range set without any per-paragraph
 * bookkeeping at the call site.
 *
 * Returns null when `pos` doesn't resolve into a textblock (e.g., position
 * 0 in an empty doc, or a position right between two blocks).
 */
export function extractParagraphAt(
  doc: any,
  pos: number
): {
  paraStart: number;
  paraEnd: number;
  plainText: string;
  segments: TextSegment[];
} | null {
  const $pos = doc.resolve(pos);
  for (let d = $pos.depth; d >= 0; d--) {
    const node = $pos.node(d);
    if (node && node.isTextblock) {
      const paraStart = $pos.before(d) + 1;
      const paraEnd = $pos.after(d) - 1;
      let plainText = "";
      const segments: TextSegment[] = [];
      node.descendants((child: any, childPos: number) => {
        if (child.isText) {
          const text: string = child.text ?? "";
          segments.push({
            pmStart: paraStart + childPos,
            plainStart: plainText.length,
            length: text.length,
          });
          plainText += text;
        }
        return true;
      });
      return { paraStart, paraEnd, plainText, segments };
    }
  }
  return null;
}

/**
 * Remap a list of stored ranges through a ProseMirror transaction mapping.
 * Ranges that collapse (zero or negative width) are dropped — that means
 * the underlying text was deleted by the transaction.
 *
 * Bias: `from` maps with +1 (stay attached to text on the right when the
 * insertion happens at the boundary), `to` with -1 (stay attached on the
 * left). This is the standard pair for "preserve the range over text the
 * user didn't actually edit."
 */
export function remapRanges<T extends { from: number; to: number; match: any }>(
  ranges: T[],
  mapping: { map(pos: number, bias?: number): number }
): T[] {
  const result: T[] = [];
  for (const r of ranges) {
    const from = mapping.map(r.from, 1);
    const to = mapping.map(r.to, -1);
    if (to > from) {
      result.push({ ...r, from, to });
    }
  }
  return result;
}

export function getDocumentChangeStats(mapping: any): DocumentChangeStats {
  const stats: DocumentChangeStats = {
    insertedChars: 0,
    deletedChars: 0,
    largestInsertionChars: 0,
    largestDeletionChars: 0,
    changed: false,
  };

  const maps = Array.isArray(mapping?.maps) ? mapping.maps : [];
  for (const stepMap of maps) {
    if (typeof stepMap?.forEach !== "function") continue;
    stepMap.forEach(
      (
        oldStart: number,
        oldEnd: number,
        newStart: number,
        newEnd: number
      ) => {
        const deleted = Math.max(0, oldEnd - oldStart);
        const inserted = Math.max(0, newEnd - newStart);
        if (deleted > 0 || inserted > 0) stats.changed = true;
        stats.deletedChars += deleted;
        stats.insertedChars += inserted;
        stats.largestDeletionChars = Math.max(
          stats.largestDeletionChars,
          deleted
        );
        stats.largestInsertionChars = Math.max(
          stats.largestInsertionChars,
          inserted
        );
      }
    );
  }

  return stats;
}

export function plainToPmPos(
  plainOffset: number,
  segments: TextSegment[]
): number | null {
  for (const seg of segments) {
    if (plainOffset >= seg.plainStart && plainOffset <= seg.plainStart + seg.length) {
      return seg.pmStart + (plainOffset - seg.plainStart);
    }
  }
  return null;
}

export function plainOffsetForPmPos(
  pmPos: number,
  segments: TextSegment[]
): number | null {
  if (segments.length === 0) return null;

  let nearestPlainOffset: number | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const seg of segments) {
    const segPmEnd = seg.pmStart + seg.length;
    if (pmPos >= seg.pmStart && pmPos <= segPmEnd) {
      return seg.plainStart + (pmPos - seg.pmStart);
    }

    const beforeDistance = Math.abs(pmPos - seg.pmStart);
    if (beforeDistance < nearestDistance) {
      nearestDistance = beforeDistance;
      nearestPlainOffset = seg.plainStart;
    }

    const afterDistance = Math.abs(pmPos - segPmEnd);
    if (afterDistance < nearestDistance) {
      nearestDistance = afterDistance;
      nearestPlainOffset = seg.plainStart + seg.length;
    }
  }

  return nearestPlainOffset;
}

export function extractTextWindowAroundPmPos(
  extracted: ExtractedText,
  pmPos: number,
  maxChars: number
): ExtractedTextWindow | null {
  if (!extracted.plainText || maxChars <= 0 || extracted.segments.length === 0) {
    return null;
  }

  const centerPlainOffset = plainOffsetForPmPos(pmPos, extracted.segments);
  if (centerPlainOffset == null) return null;

  const windowChars = Math.min(maxChars, extracted.plainText.length);
  const halfWindow = Math.floor(windowChars / 2);
  let plainStart = Math.max(0, centerPlainOffset - halfWindow);
  let plainEnd = Math.min(extracted.plainText.length, plainStart + windowChars);
  plainStart = Math.max(0, plainEnd - windowChars);

  const segments: TextSegment[] = [];
  for (const seg of extracted.segments) {
    const segPlainStart = seg.plainStart;
    const segPlainEnd = seg.plainStart + seg.length;
    const overlapStart = Math.max(segPlainStart, plainStart);
    const overlapEnd = Math.min(segPlainEnd, plainEnd);
    if (overlapEnd <= overlapStart) continue;

    segments.push({
      pmStart: seg.pmStart + (overlapStart - segPlainStart),
      plainStart: overlapStart - plainStart,
      length: overlapEnd - overlapStart,
    });
  }

  if (segments.length === 0) return null;

  const pmStart = Math.min(...segments.map((seg) => seg.pmStart));
  const pmEnd = Math.max(...segments.map((seg) => seg.pmStart + seg.length));

  return {
    plainText: extracted.plainText.slice(plainStart, plainEnd),
    plainStart,
    plainEnd,
    pmStart,
    pmEnd,
    segments,
  };
}

export function plainWindowToPmRange(
  segments: TextSegment[],
  plainStart: number,
  plainEnd: number
): { pmStart: number; pmEnd: number } | null {
  const overlapping: Array<{ pmStart: number; pmEnd: number }> = [];
  for (const seg of segments) {
    const segPlainStart = seg.plainStart;
    const segPlainEnd = seg.plainStart + seg.length;
    const overlapStart = Math.max(segPlainStart, plainStart);
    const overlapEnd = Math.min(segPlainEnd, plainEnd);
    if (overlapEnd <= overlapStart) continue;
    overlapping.push({
      pmStart: seg.pmStart + (overlapStart - segPlainStart),
      pmEnd: seg.pmStart + (overlapEnd - segPlainStart),
    });
  }

  if (overlapping.length === 0) return null;
  return {
    pmStart: Math.min(...overlapping.map((range) => range.pmStart)),
    pmEnd: Math.max(...overlapping.map((range) => range.pmEnd)),
  };
}

function segmentIndexFor(plainOffset: number, segments: TextSegment[]): number {
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (plainOffset >= seg.plainStart && plainOffset <= seg.plainStart + seg.length) {
      return i;
    }
  }
  return -1;
}

export function matchesToDecorationRanges(
  matches: GrammarMatch[],
  segments: TextSegment[]
): Array<{ from: number; to: number; match: GrammarMatch }> {
  const result: Array<{ from: number; to: number; match: GrammarMatch }> = [];
  for (const m of matches) {
    const fromIdx = segmentIndexFor(m.offset, segments);
    const toIdx = segmentIndexFor(m.offset + m.length, segments);
    if (fromIdx === -1 || toIdx === -1 || fromIdx !== toIdx) continue;
    const seg = segments[fromIdx];
    const from = seg.pmStart + (m.offset - seg.plainStart);
    const to = seg.pmStart + (m.offset + m.length - seg.plainStart);
    if (to > from) {
      result.push({ from, to, match: m });
    }
  }
  return result;
}

export async function checkGrammar(
  text: string,
  options: GrammarCheckOptions = {}
): Promise<GrammarCheckResponse> {
  if (!text || !text.trim()) return { matches: [] };

  const body: Record<string, unknown> = { text };
  if (options.language) body.language = options.language;
  if (options.styleRules) body.styleRules = options.styleRules;
  if (options.source) body.source = options.source;

  // Use the Firebase-aware helper so the server can authenticate the request.
  // Auth.fetchWithIdToken injects the idToken into the body and returns the
  // already-parsed JSON. Imported dynamically to avoid pulling Firebase into
  // unit-test code paths (the test file stubs fetch directly).
  const Auth = (await import("../../Auth.js")).default;
  const response = await Auth.fetchWithIdToken(
    "/api/grammar/check",
    body,
    options.signal ?? null,
    { suppressAbortSentry: true }
  );
  return response as GrammarCheckResponse;
}

export async function checkGrammarProgressively(
  text: string,
  options: GrammarCheckOptions = {},
  callbacks: {
    onChunk?: (chunk: GrammarProgressChunk) => void;
    signal?: AbortSignal | null;
  } = {}
): Promise<GrammarCheckResponse> {
  if (!text || !text.trim()) return { matches: [] };

  const chunks = splitTextForGrammarProgressCache(text);
  const cachedChunks = getCachedGrammarProgressChunks(text, options);
  const cachedByIndex = new Map(
    cachedChunks.map((chunk) => [chunk.chunkIndex, chunk])
  );
  const cachedMatches: GrammarMatch[] = [];
  for (const cached of cachedChunks) {
    cachedMatches.push(...cached.matches);
    callbacks.onChunk?.({
      type: "chunk",
      chunkIndex: cached.chunkIndex,
      chunkCount: cached.chunkCount,
      offset: cached.offset,
      length: cached.length,
      matches: cached.matches,
      textHash: cached.textHash,
      cached: true,
    });
  }

  if (cachedChunks.length === chunks.length && chunks.length > 0) {
    return { matches: cachedMatches };
  }

  const body: Record<string, unknown> = { text };
  if (options.language) body.language = options.language;
  if (options.styleRules) body.styleRules = options.styleRules;
  if (options.source) body.source = options.source;
  if (cachedChunks.length) body.cachedChunks = cachedChunks;

  const Auth = (await import("../../Auth.js")).default;
  return new Promise<GrammarCheckResponse>((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    Auth.streamWithIdToken(
      "/api/grammar/check-stream",
      body,
      callbacks.signal ?? null,
      (event: GrammarProgressEvent) => {
        if (event.type === "chunk") {
          if (event.cached && cachedByIndex.has(event.chunkIndex)) {
            return;
          }
          const sourceChunk = chunks[event.chunkIndex];
          if (
            sourceChunk &&
            event.offset === sourceChunk.offset &&
            event.length === sourceChunk.text.length
          ) {
            const cachedChunk = {
              chunkIndex: event.chunkIndex,
              chunkCount: event.chunkCount,
              offset: event.offset,
              length: event.length,
              textHash: sourceChunk.textHash,
              matches: event.matches || [],
            };
            writeGrammarProgressChunkCache(
              buildGrammarCheckCacheKey({
                text,
                language: options.language,
                styleRules: options.styleRules,
              }),
              cachedChunk
            );
            event = { ...event, textHash: sourceChunk.textHash };
          }
          callbacks.onChunk?.(event);
          return;
        }
        if (event.type === "done") {
          settle(() => resolve({ matches: event.matches || [] }));
        }
      },
      () => {
        settle(() =>
          reject(new Error("Grammar streaming check ended before final result")),
        );
      },
      (error: { message?: string } | Error) => {
        if (callbacks.signal?.aborted) {
          settle(() => {
            const aborted = new Error("Aborted");
            aborted.name = "AbortError";
            reject(aborted);
          });
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : error?.message || "Grammar streaming check failed";
        settle(() => reject(new Error(message)));
      }
    );
  });
}

function hashString(input: string): string {
  let h1 = 0xdeadbeef ^ input.length;
  let h2 = 0x41c6ce57 ^ input.length;

  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return `${(h2 >>> 0).toString(36)}${(h1 >>> 0).toString(36)}`;
}

interface GrammarProgressTextChunk {
  text: string;
  offset: number;
  textHash: string;
}

interface CachedGrammarProgressChunk {
  chunkIndex: number;
  chunkCount: number;
  offset: number;
  length: number;
  textHash: string;
  matches: GrammarMatch[];
}

interface GrammarProgressCacheEntry {
  key: string;
  updatedAt: number;
  chunks: Record<string, CachedGrammarProgressChunk>;
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function splitTextForGrammarProgressCache(
  text: string,
  maxChars: number = GRAMMAR_PROGRESS_CHUNK_MAX_CHARS
): GrammarProgressTextChunk[] {
  if (!text || text.length <= maxChars) {
    return [{ text, offset: 0, textHash: hashString(text) }];
  }

  const chunks: GrammarProgressTextChunk[] = [];
  let start = 0;

  while (start < text.length) {
    let end = Math.min(start + maxChars, text.length);

    if (end < text.length) {
      const minBoundary = start + Math.floor(maxChars * 0.55);
      const paragraphBreak = text.lastIndexOf("\n\n", end);
      if (paragraphBreak > minBoundary) {
        end = paragraphBreak + 2;
      } else {
        const sentenceBreak = text.lastIndexOf(". ", end);
        if (sentenceBreak > minBoundary) {
          end = sentenceBreak + 2;
        }
      }
    }

    if (end <= start) {
      end = Math.min(start + maxChars, text.length);
    }

    const chunkText = text.slice(start, end);
    chunks.push({
      text: chunkText,
      offset: start,
      textHash: hashString(chunkText),
    });
    start = end;
  }

  return chunks;
}

function grammarProgressCacheStorageKey(key: string): string {
  return `${GRAMMAR_PROGRESS_CACHE_PREFIX}${key}`;
}

function readGrammarProgressCache(key: string): GrammarProgressCacheEntry | null {
  const storage = safeLocalStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(grammarProgressCacheStorageKey(key));
    if (!raw) return null;
    const entry = JSON.parse(raw) as Partial<GrammarProgressCacheEntry>;
    if (
      entry.key !== key ||
      typeof entry.updatedAt !== "number" ||
      !entry.chunks ||
      typeof entry.chunks !== "object"
    ) {
      return null;
    }
    if (Date.now() - entry.updatedAt > GRAMMAR_PROGRESS_CACHE_TTL_MS) {
      storage.removeItem(grammarProgressCacheStorageKey(key));
      return null;
    }
    return entry as GrammarProgressCacheEntry;
  } catch {
    return null;
  }
}

function pruneGrammarProgressCache(storage: Storage) {
  const entries: Array<{ key: string; updatedAt: number }> = [];
  for (let i = 0; i < storage.length; i++) {
    const storageKey = storage.key(i);
    if (!storageKey?.startsWith(GRAMMAR_PROGRESS_CACHE_PREFIX)) continue;
    try {
      const raw = storage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : null;
      entries.push({
        key: storageKey,
        updatedAt:
          typeof parsed?.updatedAt === "number" ? parsed.updatedAt : 0,
      });
    } catch {
      entries.push({ key: storageKey, updatedAt: 0 });
    }
  }

  entries
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(GRAMMAR_PROGRESS_CACHE_MAX_ENTRIES)
    .forEach((entry) => storage.removeItem(entry.key));
}

export function clearGrammarProgressChunkCache() {
  const storage = safeLocalStorage();
  if (!storage) return;

  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const storageKey = storage.key(i);
    if (storageKey?.startsWith(GRAMMAR_PROGRESS_CACHE_PREFIX)) {
      keys.push(storageKey);
    }
  }
  keys.forEach((key) => storage.removeItem(key));
}

function writeGrammarProgressChunkCache(
  key: string,
  chunk: CachedGrammarProgressChunk
) {
  const storage = safeLocalStorage();
  if (!storage) return;

  const existing = readGrammarProgressCache(key) || {
    key,
    updatedAt: Date.now(),
    chunks: {},
  };
  existing.updatedAt = Date.now();
  existing.chunks[String(chunk.chunkIndex)] = chunk;

  try {
    storage.setItem(
      grammarProgressCacheStorageKey(key),
      JSON.stringify(existing)
    );
    pruneGrammarProgressCache(storage);
  } catch {
    // localStorage can be unavailable or full. Progressive checks still work;
    // they just cannot resume partially completed work after a remount.
  }
}

export function getCachedGrammarProgressChunks(
  text: string,
  options: { language?: string; styleRules?: UserStyleRule[] } = {}
): CachedGrammarProgressChunk[] {
  const key = buildGrammarCheckCacheKey({
    text,
    language: options.language,
    styleRules: options.styleRules,
  });
  const chunks = splitTextForGrammarProgressCache(text);
  const entry = readGrammarProgressCache(key);
  if (!entry) return [];

  const cached: CachedGrammarProgressChunk[] = [];
  chunks.forEach((chunk, chunkIndex) => {
    const candidate = entry.chunks[String(chunkIndex)];
    if (
      candidate &&
      candidate.chunkIndex === chunkIndex &&
      candidate.chunkCount === chunks.length &&
      candidate.offset === chunk.offset &&
      candidate.length === chunk.text.length &&
      candidate.textHash === chunk.textHash &&
      Array.isArray(candidate.matches)
    ) {
      cached.push(candidate);
    }
  });
  return cached;
}

function normalizedLanguage(language?: string): string {
  return (language || "en-US").trim() || "en-US";
}

function globalStyleRuleCacheParts(styleRules: UserStyleRule[] = []): string[] {
  return styleRules
    .filter((rule) => rule && !rule.match_text && rule.rule_id && rule.action)
    .map((rule) => `${rule.rule_id}:${rule.action}`)
    .sort();
}

export function buildGrammarCheckCacheKey({
  text,
  language,
  styleRules = [],
}: GrammarCheckCacheKeyOptions): string {
  const textHash = hashString(text);
  const rulesHash = hashString(
    globalStyleRuleCacheParts(styleRules).join("\u001f")
  );
  return [
    "v2",
    normalizedLanguage(language),
    text.length.toString(36),
    textHash,
    rulesHash,
  ].join(":");
}

export async function checkGrammarWithFullDocumentCache(
  text: string,
  options: GrammarCheckOptions,
  state: FullGrammarCheckCacheState,
  check: GrammarCheckFn = checkGrammar
): Promise<GrammarCheckResponse> {
  const key = buildGrammarCheckCacheKey({
    text,
    language: options.language,
    styleRules: options.styleRules,
  });

  if (state.cache?.key === key) {
    return state.cache.response;
  }

  if (state.inFlight?.key === key) {
    return state.inFlight.promise;
  }

  const promise = check(text, options);
  state.inFlight = { key, promise };
  try {
    const response = await promise;
    if (state.inFlight?.key === key) {
      state.cache = { key, response };
    }
    return response;
  } finally {
    if (state.inFlight?.key === key) {
      state.inFlight = null;
    }
  }
}

/**
 * Fetch the user's saved spell+grammar preferences. Called once when the
 * editor mounts and the grammar/spell toggle first turns on. Replaces the
 * three per-keystroke Prisma queries that used to live on the server side
 * of /api/grammar/check.
 */
export async function fetchGrammarPreferences(): Promise<GrammarPreferences> {
  const Auth = (await import("../../Auth.js")).default;
  const response = (await Auth.fetchWithIdToken(
    "/api/grammar/preferences"
  )) as Partial<GrammarPreferences> | undefined;
  return {
    dictionary: Array.isArray(response?.dictionary) ? response!.dictionary : [],
    styleRules: Array.isArray(response?.styleRules) ? response!.styleRules : [],
  };
}

/**
 * Split a character's `other_names` free-text field into individual tokens.
 * Users typically enter aliases comma- or newline-separated; we accept either.
 *
 * Ported verbatim from `src/routes/grammar.js` so the same tokenization that
 * used to happen on the server now happens on the client.
 */
export function tokenizeOtherNames(otherNames: unknown): string[] {
  if (!otherNames || typeof otherNames !== "string") return [];
  return otherNames
    .split(/[,\n]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/**
 * Expand a character name (or alias) into dictionary entries.
 *
 * LT flags individual word tokens, not phrases, so "Vaun Leary" stored as
 * one phrase doesn't suppress "Vaun" on its own. Push both the full phrase
 * (cheap, and useful if anything ever matches phrases) and each
 * whitespace-separated word.
 *
 * Ported verbatim from `src/routes/grammar.js`.
 */
export function expandNameTokens(name: unknown): string[] {
  if (!name || typeof name !== "string") return [];
  const trimmed = name.trim();
  if (!trimmed) return [];
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [trimmed];
  return [trimmed, ...words];
}

/**
 * Filter LT matches against the user's saved dictionary and per-phrase
 * style-rule overrides. Mirrors the logic that used to live in
 * `src/lib/LanguageTool.js#filterMatches`, but operates on the normalized
 * `GrammarMatch` shape (`ruleId`, not `rule.id`).
 *
 *   - dictionary: case-insensitive whole-substring match against the
 *     text slice covered by the match's offset/length.
 *   - possessiveBases: Story Bible name tokens that may appear with a
 *     possessive suffix. This is intentionally separate from dictionary so
 *     ordinary saved words do not hide plural typos.
 *   - styleRules with `match_text`: dismiss matches whose ruleId AND
 *     exact substring both equal an entry in the rules list. styleRules
 *     without `match_text` are ignored here (they're sent to LT directly
 *     as disabledRules/enabledRules).
 */
/** Product names that should never appear as spelling errors, regardless of user dictionary. */
const SUPPRESSED_SPELLING_WORDS: ReadonlySet<string> = new Set(["sudowrite"]);

function normalizeFilterToken(token: string): string {
  return token.trim().toLowerCase();
}

function allowsBareSPossessive(language: string): boolean {
  return /^(de|sv|da|no|nb|nn)(?:-|$)/i.test(language);
}

function matchesPossessiveBase(
  matched: string,
  possessiveBaseSet: ReadonlySet<string>,
  language: string
): boolean {
  if (possessiveBaseSet.size === 0) return false;

  const normalized = normalizeFilterToken(matched);
  if (!normalized) return false;

  const bareSBase = normalized.endsWith("s") ? normalized.slice(0, -1) : "";
  return Boolean(
    allowsBareSPossessive(language) &&
    bareSBase &&
      possessiveBaseSet.has(bareSBase)
  );
}

export function filterMatches({
  text,
  matches,
  dictionary = [],
  possessiveBases = [],
  language = "en-US",
  styleRules = [],
}: {
  text: string;
  matches: GrammarMatch[];
  dictionary?: string[];
  possessiveBases?: string[];
  language?: string;
  styleRules?: UserStyleRule[];
}): GrammarMatch[] {
  const dictSet = new Set(dictionary.map(normalizeFilterToken).filter(Boolean));
  const possessiveBaseSet = new Set(
    possessiveBases.map(normalizeFilterToken).filter(Boolean)
  );
  const matchTextOffs = styleRules.filter(
    (r) => r.action === "off" && r.match_text
  );

  return matches.filter((m) => {
    const matched = text.substring(m.offset, m.offset + m.length);
    if (dictSet.has(normalizeFilterToken(matched))) return false;
    if (matchesPossessiveBase(matched, possessiveBaseSet, language)) return false;
    if (
      matchTextOffs.some(
        (r) => r.rule_id === m.ruleId && r.match_text === matched
      )
    ) {
      return false;
    }
    if (m.kind === "spell" && SUPPRESSED_SPELLING_WORDS.has(normalizeFilterToken(matched))) {
      return false;
    }
    return true;
  });
}
